import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Bars2Icon } from '@heroicons/react/24/solid'
import { Link, router, usePage } from '@inertiajs/react'

import { MotionDiv } from '@/lib/framer'
import { Button } from '@/components/ui/button'
import { changelogPath, dashboardPath, meetingWastePath, rootPath, signInPath, signOutPath } from '@/routes'
import { Logo } from './logo'

function MeetingWasteNavLink({ className, onClick }: { className?: string; onClick?: () => void }) {
  return (
    <Link href={meetingWastePath()} className={className} onClick={onClick}>
      Meeting Cost Calculator
    </Link>
  )
}

function ChangelogNavLink({ className, onClick }: { className?: string; onClick?: () => void }) {
  return (
    <Link href={changelogPath()} className={className} onClick={onClick}>
      Changelog
    </Link>
  )
}

function useAuthNav() {
  const { currentUser, currentOrganization } = usePage().props
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  return {
    pathname,
    isSignedIn: !!currentUser,
    isPending: false,
    orgId: currentOrganization?.id ?? null,
    signOut: () => router.delete(signOutPath()),
  }
}

function DesktopNav() {
  const { pathname, isSignedIn, isPending, signOut } = useAuthNav()

  return (
    <nav className="hidden items-center lg:flex">
      {!isPending && !isSignedIn && (
        <>
          <Link
            href="/#features"
            className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4"
            onClick={(e) => {
              e.preventDefault()
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            Features
          </Link>
          <Link
            href="/#pricing"
            className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4"
            onClick={(e) => {
              e.preventDefault()
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            Pricing
          </Link>
          <Link
            href="/#faq"
            className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4"
            onClick={(e) => {
              e.preventDefault()
              document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            FAQ
          </Link>
          <MeetingWasteNavLink className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4" />
          <ChangelogNavLink className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4" />
          <Link
            href={signInPath()}
            className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4"
          >
            Sign In
          </Link>
        </>
      )}
      {!/^\/dashboard$|^\/setup$/.test(pathname) && isSignedIn && (
        <>
          <Link
            href={dashboardPath()}
            className="text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-base font-medium bg-blend-multiply hover:bg-black/[2.5%] hover:underline hover:underline-offset-4"
          >
            Workspace
          </Link>
          {pathname !== '/dashboard' && (
            <Link href={dashboardPath()}>
              <Button variant="default">Dashboard</Button>
            </Link>
          )}
          <button
            type="button"
            onClick={() => signOut()}
            className="font-sm text-foreground hover:decoration-[#E6A91A] flex items-center px-4 py-3 text-sm whitespace-nowrap hover:underline hover:underline-offset-4"
          >
            Sign Out
          </button>
        </>
      )}
    </nav>
  )
}

function MobileNavButton() {
  return (
    <DisclosureButton
      className="flex size-12 items-center justify-center self-center rounded-lg hover:bg-black/5 lg:hidden"
      aria-label="Open main menu"
    >
      <Bars2Icon className="size-6" />
    </DisclosureButton>
  )
}

function MobileNav({ onNavigate }: { onNavigate: () => void }) {
  const { pathname, isSignedIn, isPending, signOut } = useAuthNav()

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const headerOffset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
    onNavigate()
  }

  return (
    <DisclosurePanel className="lg:hidden">
      <div className="flex flex-col gap-6 py-4">
        {!isPending && !isSignedIn && (
          <>
            {(
              [
                ['features', 'Features'],
                ['pricing', 'Pricing'],
                ['faq', 'FAQ'],
              ] as const
            ).map(([id, label]) => (
              <MotionDiv
                key={id}
                initial={{ opacity: 0, rotateX: -90 }}
                animate={{ opacity: 1, rotateX: 0 }}
                transition={{
                  duration: 0.15,
                  ease: 'easeInOut',
                  rotateX: { duration: 0.3, delay: 0.1 },
                }}
                className="flex w-full items-center justify-center"
              >
                <Link
                  href={`/#${id}`}
                  className="text-foreground text-base font-medium hover:underline"
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToSection(id)
                  }}
                >
                  {label}
                </Link>
              </MotionDiv>
            ))}
            <MotionDiv
              initial={{ opacity: 0, rotateX: -90 }}
              animate={{ opacity: 1, rotateX: 0 }}
              transition={{
                duration: 0.15,
                ease: 'easeInOut',
                rotateX: { duration: 0.3, delay: 0.1 },
              }}
              className="flex w-full items-center justify-center"
            >
              <MeetingWasteNavLink
                className="text-foreground text-base font-medium hover:underline"
                onClick={() => onNavigate()}
              />
            </MotionDiv>
            <MotionDiv
              initial={{ opacity: 0, rotateX: -90 }}
              animate={{ opacity: 1, rotateX: 0 }}
              transition={{
                duration: 0.15,
                ease: 'easeInOut',
                rotateX: { duration: 0.3, delay: 0.1 },
              }}
              className="flex w-full items-center justify-center"
            >
              <ChangelogNavLink
                className="text-foreground text-base font-medium hover:underline"
                onClick={() => onNavigate()}
              />
            </MotionDiv>
            <MotionDiv
              initial={{ opacity: 0, rotateX: -90 }}
              animate={{ opacity: 1, rotateX: 0 }}
              transition={{
                duration: 0.15,
                ease: 'easeInOut',
                rotateX: { duration: 0.3, delay: 0.1 },
              }}
              className="flex w-full items-center justify-center"
            >
              <Link href={signInPath()} className="text-foreground text-base font-medium hover:underline">
                Sign In
              </Link>
            </MotionDiv>
          </>
        )}
        {isSignedIn && (
          <MotionDiv
            initial={{ opacity: 0, rotateX: -90 }}
            animate={{ opacity: 1, rotateX: 0 }}
            transition={{
              duration: 0.15,
              ease: 'easeInOut',
              rotateX: { duration: 0.3, delay: 0.1 },
            }}
            className="flex w-full items-center justify-center"
          >
            <div className="flex w-full flex-col gap-4">
              <Link
                href={dashboardPath()}
                className="text-foreground text-center text-base font-medium hover:underline"
                onClick={() => onNavigate()}
              >
                Workspace
              </Link>
              {pathname !== '/dashboard' && (
                <Link href={dashboardPath()}>
                  <Button variant="default" className="w-full">
                    Dashboard
                  </Button>
                </Link>
              )}
              <button
                type="button"
                onClick={() => signOut()}
                className="font-sm text-foreground hover:decoration-[#E6A91A] flex justify-center px-4 py-3 text-center text-sm hover:underline hover:underline-offset-4"
              >
                Sign Out
              </button>
            </div>
          </MotionDiv>
        )}
      </div>
      <div className="absolute left-1/2 w-screen -translate-x-1/2">
        <div className="border-border absolute inset-x-0 top-2 border-t shadow-xs" />
      </div>
    </DisclosurePanel>
  )
}

export function Navbar({ banner }: { banner?: React.ReactNode }) {
  return (
    <Disclosure as="header" className="py-4">
      {({ close }) => (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link href={rootPath()} title="Home">
                <Logo className="text-3xl" />
              </Link>
              {banner && <div className="relative hidden items-center py-3 lg:flex">{banner}</div>}
            </div>
            <DesktopNav />
            <MobileNavButton />
          </div>
          <MobileNav onNavigate={() => close()} />
        </>
      )}
    </Disclosure>
  )
}
